import { useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ComboBox, ComboBoxItem, FlexBox, Text } from '@ui5/webcomponents-react';
import { odataStr, type LookupDef } from '@b1links/core';
import { api, type Rec } from '../api/client';
import { ScanButton, scanSupported } from '../components/BarcodeScanner';

interface Props { lookup: LookupDef; value: unknown; disabled?: boolean; placeholder?: string; onSelect(row: Rec | undefined, key: string): void }

/** Search-as-you-type value help over any entity via the generic data API. */
export function LookupInput({ lookup, value, disabled, placeholder, onSelect }: Props) {
  const [text, setText] = useState('');
  const srcKey = lookup.searchKey ?? lookup.key;   // field to read on the SOURCE entity (may differ from the target field)
  const seen = useRef(new Map<string, Rec>()); // every row ever listed, so a selection resolves even after the query key changed
  const [scanError, setScanError] = useState<string | null>(null);
  // Only needed to know whether the looked-up entity carries a BarCode field (items do).
  const entity = useQuery({ queryKey: ['meta', lookup.entity], queryFn: () => api.entityMeta(lookup.entity), enabled: !disabled && scanSupported(), staleTime: Infinity });

  /** Resolve a scanned code against the key field and, where the entity has one, its barcode. */
  const onScan = async (code: string) => {
    setScanError(null);
    const fields = [srcKey, ...(entity.data?.fields.some(f => f.name === 'BarCode') ? ['BarCode'] : [])];
    const filter = [lookup.filter, `(${fields.map(f => `${f} eq ${odataStr(code)}`).join(' or ')})`].filter(Boolean).join(' and ');
    try {
      const res = await api.list(lookup.entity, { filter, select: [...new Set([srcKey, lookup.display, ...Object.values(lookup.copy ?? {})])].join(','), top: 1 });
      const row = res.value[0];
      if (row) { seen.current.set(String(row[srcKey]), row); onSelect(row, String(row[srcKey])); }
      else { setText(code); setScanError(`No ${lookup.entity} matches "${code}"`); }
    } catch (e) { setScanError((e as Error).message); }
  };
  const q = useQuery({
    queryKey: ['lookup', lookup.entity, lookup.filter, text],
    queryFn: () => api.list(lookup.entity, { q: text, filter: lookup.filter, select: [...new Set([srcKey, lookup.display, ...Object.values(lookup.copy ?? {})])].join(','), top: 20, searchFields: `${srcKey},${lookup.display}` }),
    enabled: !disabled, placeholderData: keepPreviousData,
  });
  for (const r of q.data?.value ?? []) seen.current.set(String(r[srcKey]), r);

  return (
    <FlexBox direction="Column" style={{ width: '100%' }}>
      <FlexBox alignItems="Center" style={{ gap: '0.25rem', width: '100%' }}>
        <ComboBox value={value != null && value !== '' ? String(value) : text} disabled={disabled} placeholder={placeholder ?? `Search ${lookup.entity}…`} style={{ width: '100%' }}
          onInput={e => {
            const v = (e.target as unknown as { value: string }).value;
            if (v === '' && value) { onSelect(undefined, ''); setText(''); return; }   // user cleared the field
            if (!v.includes(' – ')) setText(v);                                         // ignore the synthetic input fired on selection
          }}
          onSelectionChange={e => { const k = e.detail.item?.dataset.key ?? ''; if (k) onSelect(seen.current.get(k), k); }}>
          {q.data?.value.map(r => <ComboBoxItem key={String(r[srcKey])} text={`${r[srcKey]} – ${r[lookup.display] ?? ''}`} data-key={String(r[srcKey])} />)}
        </ComboBox>
        {!disabled && <ScanButton tooltip={`Scan a barcode to pick the ${lookup.entity === 'Items' ? 'item' : 'record'}`} onCode={onScan} />}
      </FlexBox>
      {scanError && <Text style={{ color: 'var(--sapNegativeTextColor)', fontSize: '0.75rem' }}>{scanError}</Text>}
    </FlexBox>
  );
}
